import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from './ui/card';
import CookieBanner from './CookieBanner';
import LanguageSelector from './LanguageSelector';
import Footer from './Footer';
import { useLanguage } from '../context/LanguageContext';
import './SpectacularAnimations.css';
import './TechTheme.css';

// Icons
const CheckCircle = ({ className = "" }) => (
  <svg className={`w-6 h-6 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const Shield = ({ className = "" }) => (
  <svg className={`w-6 h-6 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const Building = ({ className = "" }) => (
  <svg className={`w-6 h-6 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);

const Cpu = ({ className = "" }) => (
  <svg className={`w-6 h-6 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
  </svg>
);

const Globe = ({ className = "" }) => (
  <svg className={`w-6 h-6 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const Users = ({ className = "" }) => (
  <svg className={`w-6 h-6 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const Award = ({ className = "" }) => (
  <svg className={`w-6 h-6 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
  </svg>
);

const AiEtaireiaEllada = () => {
  const { t, language } = useLanguage();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // SEO structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "PROTOS AI AGENCY",
    "description": "AI Εταιρεία στην Ελλάδα - Λύσεις Τεχνητής Νοημοσύνης για Επιχειρήσεις",
    "url": "https://protosaiagency.gr/ai-etaireia-ellada",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "GR",
      "addressLocality": "Greece"
    },
    "areaServed": "Greece",
    "sameAs": []
  };

  return (
    <>
      <Helmet>
        <link rel="alternate" type="application/ld+json" href="https://www.wikidata.org/wiki/Special:EntityData/Q137993719.json" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "PROTOS AI Agency",
            "url": "https://ai-agency.gr",
            "sameAs": "https://www.wikidata.org/wiki/Q137993719"
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Hidden SEO Content */}
      <div className="sr-only">
        <h1>AI Εταιρεία στην Ελλάδα - PROTOS AI AGENCY - Τεχνητή Νοημοσύνη B2B</h1>
        <p>Η PROTOS AI AGENCY είναι η κορυφαία AI εταιρεία στην Ελλάδα, παρέχοντας λύσεις τεχνητής νοημοσύνης για επιχειρήσεις B2B. EU AI Pact Signatory, European Digital Sovereignty, Registered EU Expert. Πλήρης συμμόρφωση με GDPR και EU AI Act.</p>
        <p>AI εταιρεία Ελλάδα, τεχνητή νοημοσύνη Ελλάδα, AI solutions Greece, artificial intelligence Greece, AI company Greece, B2B AI services, enterprise AI solutions, GDPR compliant AI.</p>
      </div>

      <div className="min-h-screen tech-bg-primary text-white relative overflow-hidden">
        {/* Background Effects */}
        <div className="fixed inset-0 pointer-events-none z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/30 to-slate-800/50"></div>
          <svg className="absolute inset-0 w-full h-full opacity-10">
            <defs>
              <linearGradient id="circuit" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#00ffff" stopOpacity="0.3"/>
                <stop offset="50%" stopColor="#8b5cf6" stopOpacity="0.2"/>
                <stop offset="100%" stopColor="#ec4899" stopOpacity="0.3"/>
              </linearGradient>
            </defs>
            <path d="M100,50 L200,50 L200,150 L400,150" stroke="url(#circuit)" strokeWidth="1" fill="none" className="animate-pulse"/>
            <path d="M300,200 L500,200 L500,100 L700,100" stroke="url(#circuit)" strokeWidth="1" fill="none" className="animate-pulse"/>
            <path d="M600,300 L800,300 L800,200 L1000,200" stroke="url(#circuit)" strokeWidth="1" fill="none" className="animate-pulse"/>
          </svg>
        </div>

        {/* Header */}
        <header className="tech-header sticky top-0 z-50 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center space-x-3">
                <Link to="/" className="cursor-pointer hover:opacity-80 transition-opacity duration-300">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_globalized-site/artifacts/nb42ugut_PROTOS%20AI%20AGency.png" 
                    alt="Protos AI Agency Logo"
                    className="w-32 h-20 md:w-48 md:h-28 object-contain"
                  />
                </Link>
              </div>

              {/* Desktop Navigation */}
              <nav className="hidden md:flex space-x-8 items-center">
                <Link to="/" className="tech-nav-link">{t('nav.home')}</Link>
                
                {/* Services Dropdown */}
                <div className="relative group">
                  <a href="/#services" className="tech-nav-link">
                    {t('nav.services')}
                    <svg className="w-4 h-4 ml-1 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </a>
                  <div className="absolute left-0 mt-2 w-64 bg-gray-900/95 backdrop-blur-sm border border-white/10 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="py-2">
                      <Link to="/services/dimosia-dioikisi" className="block px-4 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-blue-400">{t('services.dimosiaDioikisi')}</Link>
                      <Link to="/services/asfaleia" className="block px-4 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-blue-400">{t('services.asfaleia')}</Link>
                      <Link to="/services/politistiki-klironomia" className="block px-4 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-blue-400">{t('services.politistikiKlironomia')}</Link>
                      <Link to="/services/ygeia-ekpaidefsi" className="block px-4 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-blue-400">{t('services.ygeiaEkpaidefsi')}</Link>
                      <Link to="/services/erevna-anaptyxi" className="block px-4 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-blue-400">{t('services.erevnaAnaptyxi')}</Link>
                      <Link to="/services/green-horizon" className="block px-4 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-blue-400">{t('services.greenHorizon')}</Link>
                    </div>
                  </div>
                </div>
                
                <Link to="/athena" className="tech-nav-link">{t('nav.athena')}</Link>
                <Link to="/matia" className="tech-nav-link">{t('nav.matia')}</Link>
                <Link to="/geo" className="tech-nav-link">{t('nav.geo')}</Link>
                <Link to="/ai-etaireia-ellada" className="tech-nav-link active">{t('nav.aiEtaireiaEllada')}</Link>
                <Link to="/news" className="tech-nav-link">{t('nav.news')}</Link>
                <a href="/#team" className="tech-nav-link">{t('nav.about')}</a>
                <a href="/#contact" className="tech-nav-link">{t('nav.contact')}</a>
                
                <LanguageSelector />
              </nav>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 rounded-lg text-white hover:bg-white/10 transition-colors"
                aria-label="Toggle menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>

            {/* Mobile Navigation */}
            {isMobileMenuOpen && (
              <div className="md:hidden py-4 border-t border-white/10">
                <nav className="flex flex-col space-y-2">
                  <Link to="/" className="tech-nav-link px-4 py-2 rounded-lg hover:bg-white/10" onClick={() => setIsMobileMenuOpen(false)}>
                    {t('nav.home')}
                  </Link>
                  <Link to="/athena" className="tech-nav-link px-4 py-2 rounded-lg hover:bg-white/10" onClick={() => setIsMobileMenuOpen(false)}>
                    {t('nav.athena')}
                  </Link>
                  <Link to="/matia" className="tech-nav-link px-4 py-2 rounded-lg hover:bg-white/10" onClick={() => setIsMobileMenuOpen(false)}>
                    {t('nav.matia')}
                  </Link>
                  <Link to="/geo" className="tech-nav-link px-4 py-2 rounded-lg hover:bg-white/10" onClick={() => setIsMobileMenuOpen(false)}>
                    {t('nav.geo')}
                  </Link>
                  <Link to="/ai-etaireia-ellada" className="tech-nav-link active px-4 py-2 rounded-lg hover:bg-white/10" onClick={() => setIsMobileMenuOpen(false)}>
                    {t('nav.aiEtaireiaEllada')}
                  </Link>
                  <Link to="/news" className="tech-nav-link px-4 py-2 rounded-lg hover:bg-white/10" onClick={() => setIsMobileMenuOpen(false)}>
                    {t('nav.news')}
                  </Link>
                  <LanguageSelector />
                </nav>
              </div>
            )}
          </div>
        </header>

        {/* Hero Section */}
        <section className="relative py-20 md:py-32 px-4" data-testid="ai-etaireia-hero">
          <div className="max-w-6xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight" data-testid="ai-etaireia-h1">
              <span className="text-white">{t('aiEtaireiaEllada.hero.title1')}</span>
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 mt-2">
                {t('aiEtaireiaEllada.hero.title2')}
              </span>
            </h1>
            
            <h2 className="text-base md:text-lg lg:text-xl text-cyan-300 mb-8 max-w-4xl mx-auto leading-relaxed" data-testid="ai-etaireia-h2">
              {t('aiEtaireiaEllada.hero.subtitle')}
            </h2>
          </div>
        </section>

        {/* Why PROTOS is Top AI Company Section */}
        <section className="relative py-16 px-4" data-testid="ai-etaireia-why-top">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8 text-white">
              {t('aiEtaireiaEllada.whyTop.title')}
            </h2>
            <div className="text-gray-300 text-base md:text-lg leading-relaxed space-y-4">
              <p>{t('aiEtaireiaEllada.whyTop.text1')}</p>
              <p>{t('aiEtaireiaEllada.whyTop.text2')}</p>
              <p>{t('aiEtaireiaEllada.whyTop.text3')}</p>
            </div>
          </div>
        </section>

        {/* Complete AI Solutions Section */}
        <section className="relative py-16 px-4 bg-gradient-to-b from-slate-900/30 to-transparent" data-testid="ai-etaireia-complete-solutions">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8 text-white">
              {t('aiEtaireiaEllada.completeSolutions.title')}
            </h2>
            <div className="text-gray-300 text-base md:text-lg leading-relaxed space-y-4">
              <p>{t('aiEtaireiaEllada.completeSolutions.text1')}</p>
              <p>{t('aiEtaireiaEllada.completeSolutions.text2')}</p>
              <p>{t('aiEtaireiaEllada.completeSolutions.text3')}</p>
              <p>{t('aiEtaireiaEllada.completeSolutions.text4')}</p>
              <p>{t('aiEtaireiaEllada.completeSolutions.text5')}</p>
            </div>
          </div>
        </section>

        {/* Best AI Company Section */}
        <section className="relative py-16 px-4" data-testid="ai-etaireia-best">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8 text-white">
              {t('aiEtaireiaEllada.bestCompany.title')}
            </h2>
            <div className="text-gray-300 text-base md:text-lg leading-relaxed space-y-4">
              <p>{t('aiEtaireiaEllada.bestCompany.text1')}</p>
              <p>{t('aiEtaireiaEllada.bestCompany.text2')}</p>
              <p>{t('aiEtaireiaEllada.bestCompany.text3')}</p>
              <p>{t('aiEtaireiaEllada.bestCompany.text4')}</p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="relative py-16 px-4 bg-gradient-to-b from-slate-900/30 to-transparent" data-testid="ai-etaireia-faq">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10 text-white">
              {t('aiEtaireiaEllada.faq.title')}
            </h2>
            
            <div className="space-y-6">
              {/* FAQ 1 */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-cyan-300 mb-3">
                  {t('aiEtaireiaEllada.faq.q1.question')}
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  {t('aiEtaireiaEllada.faq.q1.answer')}
                </p>
              </div>

              {/* FAQ 2 */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-cyan-300 mb-3">
                  {t('aiEtaireiaEllada.faq.q2.question')}
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  {t('aiEtaireiaEllada.faq.q2.answer')}
                </p>
              </div>

              {/* FAQ 3 */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-cyan-300 mb-3">
                  {t('aiEtaireiaEllada.faq.q3.question')}
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  {t('aiEtaireiaEllada.faq.q3.answer')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="relative py-20 px-4" data-testid="ai-etaireia-why">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                {t('aiEtaireiaEllada.whyUs.title')}
              </span>
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Card 1 - GDPR */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300">
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-xl flex items-center justify-center mb-4">
                  <Shield className="w-8 h-8 text-cyan-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{t('aiEtaireiaEllada.whyUs.gdpr.title')}</h3>
                <p className="text-gray-400">{t('aiEtaireiaEllada.whyUs.gdpr.description')}</p>
              </div>

              {/* Card 2 - ISO Certifications */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300">
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-xl flex items-center justify-center mb-4">
                  <Award className="w-8 h-8 text-cyan-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{t('aiEtaireiaEllada.whyUs.iso.title')}</h3>
                <p className="text-gray-400">{t('aiEtaireiaEllada.whyUs.iso.description')}</p>
              </div>

              {/* Card 3 - B2B Focus */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300">
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-xl flex items-center justify-center mb-4">
                  <Building className="w-8 h-8 text-cyan-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{t('aiEtaireiaEllada.whyUs.b2b.title')}</h3>
                <p className="text-gray-400">{t('aiEtaireiaEllada.whyUs.b2b.description')}</p>
              </div>

              {/* Card 4 - On-Premise */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300">
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-xl flex items-center justify-center mb-4">
                  <Cpu className="w-8 h-8 text-cyan-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{t('aiEtaireiaEllada.whyUs.onPremise.title')}</h3>
                <p className="text-gray-400">{t('aiEtaireiaEllada.whyUs.onPremise.description')}</p>
              </div>

              {/* Card 5 - Greek Market */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300">
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-xl flex items-center justify-center mb-4">
                  <Globe className="w-8 h-8 text-cyan-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{t('aiEtaireiaEllada.whyUs.greek.title')}</h3>
                <p className="text-gray-400">{t('aiEtaireiaEllada.whyUs.greek.description')}</p>
              </div>

              {/* Card 6 - Expert Team */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300">
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-xl flex items-center justify-center mb-4">
                  <Users className="w-8 h-8 text-cyan-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{t('aiEtaireiaEllada.whyUs.team.title')}</h3>
                <p className="text-gray-400">{t('aiEtaireiaEllada.whyUs.team.description')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="relative py-20 px-4 bg-gradient-to-b from-transparent to-slate-900/50" data-testid="ai-etaireia-services">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                {t('aiEtaireiaEllada.services.title')}
              </span>
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Service Items */}
              <div className="flex items-start space-x-4 p-4 bg-white/5 rounded-xl">
                <CheckCircle className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-white">{t('aiEtaireiaEllada.services.item1.title')}</h3>
                  <p className="text-gray-400 text-sm">{t('aiEtaireiaEllada.services.item1.description')}</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-white/5 rounded-xl">
                <CheckCircle className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-white">{t('aiEtaireiaEllada.services.item2.title')}</h3>
                  <p className="text-gray-400 text-sm">{t('aiEtaireiaEllada.services.item2.description')}</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-white/5 rounded-xl">
                <CheckCircle className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-white">{t('aiEtaireiaEllada.services.item3.title')}</h3>
                  <p className="text-gray-400 text-sm">{t('aiEtaireiaEllada.services.item3.description')}</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-white/5 rounded-xl">
                <CheckCircle className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-white">{t('aiEtaireiaEllada.services.item4.title')}</h3>
                  <p className="text-gray-400 text-sm">{t('aiEtaireiaEllada.services.item4.description')}</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-white/5 rounded-xl">
                <CheckCircle className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-white">{t('aiEtaireiaEllada.services.item5.title')}</h3>
                  <p className="text-gray-400 text-sm">{t('aiEtaireiaEllada.services.item5.description')}</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-white/5 rounded-xl">
                <CheckCircle className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-white">{t('aiEtaireiaEllada.services.item6.title')}</h3>
                  <p className="text-gray-400 text-sm">{t('aiEtaireiaEllada.services.item6.description')}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* EU AI Act Section */}
        <section className="relative py-20 px-4" data-testid="ai-etaireia-euaiact">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-gradient-to-r from-cyan-500/10 to-blue-600/10 border border-cyan-500/30 rounded-2xl p-8 md:p-12">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                {t('aiEtaireiaEllada.euAiAct.title')}
              </h2>
              <p className="text-gray-300 text-lg mb-6">
                {t('aiEtaireiaEllada.euAiAct.description')}
              </p>
              <div className="flex flex-wrap justify-center gap-4 mt-6">
                <span className="px-4 py-2 bg-cyan-500/20 text-cyan-300 rounded-full text-sm font-medium">GDPR</span>
                <span className="px-4 py-2 bg-cyan-500/20 text-cyan-300 rounded-full text-sm font-medium">EU AI Act</span>
                <span className="px-4 py-2 bg-cyan-500/20 text-cyan-300 rounded-full text-sm font-medium">EU AI Pact Signatory</span>
                <span className="px-4 py-2 bg-cyan-500/20 text-cyan-300 rounded-full text-sm font-medium">European Digital Sovereignty</span>
                <span className="px-4 py-2 bg-cyan-500/20 text-cyan-300 rounded-full text-sm font-medium">Registered EU Expert</span>
              </div>
            </div>
          </div>
        </section>

        <Footer />

        {/* Cookie Banner */}
        <CookieBanner />
      </div>
    </>
  );
};

export default AiEtaireiaEllada;
